<script setup>
import { computed, ref } from 'vue';
import { useTimeoutPoll } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import OnboardingAPI from 'dashboard/api/onboarding';
import CreationStatusRow from './CreationStatusRow.vue';

const { t, locale } = useI18n();
const POLL_INTERVAL = 5000;

const generation = ref({
  generation_id: null,
  state: null,
  articles_count: 0,
  categories_count: 0,
});

const status = computed(
  () =>
    generation.value.state?.status ||
    (generation.value.generation_id ? 'generating' : 'not_started')
);
const isCompleted = computed(() => status.value === 'completed');
const isSkipped = computed(() => status.value === 'skipped');
const isNotStarted = computed(() => status.value === 'not_started');
const isTerminal = computed(
  () => isCompleted.value || isSkipped.value || isNotStarted.value || ['partial', 'failed', 'cancelled'].includes(status.value)
);
const articlesCount = computed(() => generation.value.articles_count || 0);
const categoriesCount = computed(() => generation.value.categories_count || 0);

// Poll the generation status until it reaches a terminal state. useTimeoutPoll
// waits for each request to settle before scheduling the next (so requests never
// overlap), fires immediately on mount, and stops on unmount.
const { pause: stopPolling } = useTimeoutPoll(
  async () => {
    try {
      const { data } = await OnboardingAPI.getHelpCenterGeneration();
      generation.value = data;
      if (isTerminal.value) stopPolling();
    } catch {
      // Keep polling; transient network failures should not strand onboarding.
    }
  },
  POLL_INTERVAL,
  { immediate: true }
);


const articlesText = computed(() =>
  t(
    'ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER_ARTICLES',
    { count: articlesCount.value },
    articlesCount.value
  )
);

const statusText = computed(() => {
  if (['partial', 'failed', 'cancelled'].includes(status.value)) {
    return locale.value.startsWith('ru')
      ? 'Подготовка не завершена полностью. Проверьте результат и повторите в центре поддержки.'
      : 'Preparation is incomplete. Review the result and retry in the Help Center.';
  }
  if (isCompleted.value) {
    const categories = t(
      'ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER_CATEGORIES',
      { count: categoriesCount.value },
      categoriesCount.value
    );
    return t(
      'ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER_SUMMARY',
      { count: articlesCount.value, categories },
      articlesCount.value
    );
  }
  if (articlesCount.value > 0) return articlesText.value;
  return t('ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER_GENERATING');
});

const isVisible = computed(() => !isSkipped.value && !isNotStarted.value);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <CreationStatusRow
    v-if="isVisible"
    :ready="isCompleted"
    :failed="isTerminal && !isCompleted"
    :title="t('ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER')"
    :description="
      t('ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.HELP_CENTER_DESCRIPTION')
    "
    :status="statusText"
  />
</template>
